import { Badge, Layout, Spin } from "antd";
import { FormattedMessage } from "react-intl";

import { DesktopBanner } from "../components/DesktopBanner";
import {
  ContentHeader,
  type ContentHeaderProps,
} from "../components/shared/ContentHeader";
import { ItemSidebarProvider } from "../components/shared/ItemSidebar/Provider";
import { ListViewProvider } from "../components/shared/ListView/ListViewContext";
import type { PermissionsFragment } from "../lib/graphql";
import { useMediaXs } from "../lib/hooks/basic/media";
import { useCurrentUser } from "../lib/hooks/basic/users";
import { useCheckNewVersion } from "../lib/hooks/version-check";
import { Head } from "./Head";

export interface BasicLayoutProps extends Omit<ContentHeaderProps, "title"> {
  contentStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  authorize?: (p?: PermissionsFragment) => boolean;
  loading?: boolean;
  loadingTip?: React.ReactNode;
  fullContentHeight?: boolean;
  title?: string;
  ribbonTop?: number;
}

const BasicLayout: React.FunctionComponent<
  React.PropsWithChildren<BasicLayoutProps>
> = ({
  children,
  contentStyle,
  headerStyle,
  authorize,
  loading: layoutLoading,
  fullContentHeight = true,
  title = "",
  ribbonTop = 12,
  loadingTip,
  ...headerProps
}) => {
  const { useAuthorized, currentTenant } = useCurrentUser();
  const { user } = useAuthorized(authorize);
  const isXs = useMediaXs();

  useCheckNewVersion({ skip: !user });

  const content = (
    <Layout.Content
      style={{
        margin: isXs ? "16px 4px" : "16px",
        padding: isXs ? 10 : 24,
        background: "var(--ant-color-bg-container)",
        minHeight: "75vh",
        position: "relative",
        flex: fullContentHeight ? "auto" : "none",
        display: "flex",
        flexDirection: "column",
        ...contentStyle,
      }}
    >
      {children}
    </Layout.Content>
  );

  return (
    <>
      {user && <Head title={title} />}

      <DesktopBanner />

      {!user || layoutLoading ? (
        <Spin
          size="large"
          description={loadingTip}
          style={{ marginTop: "40vh" }}
        />
      ) : (
        <ListViewProvider>
          <ItemSidebarProvider>
            <ContentHeader style={headerStyle} {...headerProps} />
            {currentTenant.liveTenantId ? (
              <Badge.Ribbon
                text={
                  <FormattedMessage
                    id="tenants.testMode"
                    defaultMessage="Test Mode"
                  />
                }
                color="#b42424"
                style={{ right: 8, top: ribbonTop }}
              >
                {content}
              </Badge.Ribbon>
            ) : (
              content
            )}
          </ItemSidebarProvider>
        </ListViewProvider>
      )}
    </>
  );
};

export default BasicLayout;
